import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plane, Calendar, Users, ArrowRight, AlertCircle, Plus, Minus, User, Baby } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookingModalNew from '../components/BookingModalNew';
import { routes as mockRoutes } from '../data/mock';
import { api } from '../utils/apiClient';
import errorLogger from '../utils/errorHandler';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import { FadeInWhenVisible, StaggerContainer, StaggerItem } from '../components/CinematicEffects';

const RoutesPage = () => {
  const { t } = useLanguage();
  const [routes, setRoutes] = useState(mockRoutes);
  const [searchParams, setSearchParams] = useState({
    origin: '',
    destination: '',
    date: '',
    returnDate: '',
    adults: 1,
    children: 0,
    babies: 0,
    tripType: 'OW'
  });
  const [searchResults, setSearchResults] = useState([]);
  const [returnFlights, setReturnFlights] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isPassengerPopoverOpen, setIsPassengerPopoverOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Booking modal state
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [selectedOutboundFlight, setSelectedOutboundFlight] = useState(null);
  const [selectedReturnFlight, setSelectedReturnFlight] = useState(null);
  
  // Calcular total de pasajeros
  const totalPassengers = searchParams.adults + searchParams.children + searchParams.babies;

  const handleSearch = (e) => {
    e.preventDefault();

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(searchParams.date + 'T00:00:00');

    if (selectedDate < today) {
      alert(t('search.selectDate'));
      return;
    }


    const query = new URLSearchParams({
      tipo_vuelo: searchParams.tripType,
      origen: searchParams.origin,
      fecha_ida: searchParams.date,
      destino: searchParams.destination,
      fecha_vuelta: searchParams.returnDate || '',
      adulto: searchParams.adults,
      menor: searchParams.children,
      bebe: searchParams.babies,
      submit: "Buscar Vuelos ->"
    }).toString();

    window.location.href = `https://www.aerotucan.com/php/choosefly.php?${query}`;
  };

  const handleBooking = (flight, isReturnFlight = false) => {
    if (searchParams.tripType === 'OW') {
      // Para vuelo solo ida, abrir modal directamente
      setSelectedFlight(flight);
      setIsBookingModalOpen(true);
    } else {
      // Para viaje redondo, necesitamos seleccionar ambos vuelos
      if (!isReturnFlight) {
        // Seleccionando vuelo de ida
        setSelectedOutboundFlight(flight);
        alert('Vuelo de ida seleccionado. Ahora selecciona tu vuelo de regreso.');
      } else {
        // Seleccionando vuelo de regreso
        setSelectedReturnFlight(flight);
        // Abrir modal con ambos vuelos
        setIsBookingModalOpen(true);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section with Search */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white overflow-hidden">
        {/* Background with cinematic overlay */}
        <div className="absolute inset-0 z-0">
          <div 
            className="ken-burns absolute inset-0"
            style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_oaxaca-airways-dev/artifacts/y9a7e10n_PHOTO-2026-01-27-08-51-56%202.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-blue-800/90 to-gray-900/95"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('hero.findFlight')}
            </h1>
            <p className="text-xl text-blue-100">
              {t('hero.bookFlights')}
            </p>
          </motion.div>

          {/* Buscador de Vuelos */}
          <Card className="max-w-5xl mx-auto shadow-2xl border-0">
            <CardContent className="p-8">
              <form onSubmit={handleSearch}>
                {/* Tipo de Viaje */}
                <div className="mb-6">
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        value="OW"
                        checked={searchParams.tripType === 'OW'}
                        onChange={(e) => setSearchParams({...searchParams, tripType: e.target.value, returnDate: ''})}
                        className="mr-2 w-4 h-4 text-blue-900"
                      />
                      <span className="text-gray-700 font-semibold">{t('search.oneWay')}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        value="RT"
                        checked={searchParams.tripType === 'RT'}
                        onChange={(e) => setSearchParams({...searchParams, tripType: e.target.value})}
                        className="mr-2 w-4 h-4 text-blue-900"
                      />
                      <span className="text-gray-700 font-semibold">{t('search.roundTrip')}</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div>
                    <Label htmlFor="origin" className="text-gray-700 font-semibold mb-2 block">
                      {t('search.origin')}
                    </Label>
                    <select
                      id="origin"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={searchParams.origin}
                      onChange={(e) => setSearchParams({...searchParams, origin: e.target.value})}
                      required
                    >
                      <option value="">{t('search.selectOrigin')}</option>
                      <option value="Oaxaca">Oaxaca de Juárez</option>
                      <option value="Puerto Escondido">Puerto Escondido</option>
                      <option value="Huatulco">Huatulco</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="destination" className="text-gray-700 font-semibold mb-2 block">
                      {t('search.destination')}
                    </Label>
                    <select
                      id="destination"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={searchParams.destination}
                      onChange={(e) => setSearchParams({...searchParams, destination: e.target.value})}
                      required
                    >
                      <option value="">{t('search.selectDestination')}</option>
                      <option value="Oaxaca">Oaxaca de Juárez</option>
                      <option value="Puerto Escondido">Puerto Escondido</option>
                      <option value="Huatulco">Huatulco</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="date" className="text-gray-700 font-semibold mb-2 block">
                      {t('search.date')}
                    </Label>
                    <Input
                      id="date"
                      type="date"
                      className="px-4 py-3"
                      value={searchParams.date}
                      onChange={(e) => setSearchParams({...searchParams, date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  {searchParams.tripType === 'RT' && (
                    <div>
                      <Label htmlFor="returnDate" className="text-gray-700 font-semibold mb-2 block">
                        {t('search.returnDate')}
                      </Label>
                      <Input
                        id="returnDate"
                        type="date"
                        className="px-4 py-3"
                        value={searchParams.returnDate}
                        onChange={(e) => setSearchParams({...searchParams, returnDate: e.target.value})}
                        min={searchParams.date || new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  )}

                  {searchParams.tripType === 'OW' && (
                    <div>
                      <Label htmlFor="passengers" className="text-gray-700 font-semibold mb-2 block">
                        {t('search.passengers')}
                      </Label>
                      <Popover open={isPassengerPopoverOpen} onOpenChange={setIsPassengerPopoverOpen}>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between bg-white"
                          >
                            <span className="text-gray-700">
                              {totalPassengers} {totalPassengers === 1 ? (t('search.searching') === 'Searching...' ? 'passenger' : 'pasajero') : (t('search.searching') === 'Searching...' ? 'passengers' : 'pasajeros')}
                            </span>
                            <Users className="w-5 h-5 text-gray-400" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-4" align="start">
                          <div className="space-y-4">
                            {/* Adults */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Users className="w-5 h-5 text-blue-900" />
                                <div>
                                  <p className="font-semibold text-gray-900">{t('search.adults')}</p>
                                  <p className="text-sm text-gray-500">{t('search.searching') === 'Searching...' ? '12+ years' : '12+ años'}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => searchParams.adults > 1 && setSearchParams({...searchParams, adults: searchParams.adults - 1})}
                                  disabled={searchParams.adults <= 1}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <span className="text-lg font-bold w-8 text-center">{searchParams.adults}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => totalPassengers < 13 && setSearchParams({...searchParams, adults: searchParams.adults + 1})}
                                  disabled={totalPassengers >= 13}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Children */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <User className="w-5 h-5 text-amber-600" />
                                <div>
                                  <p className="font-semibold text-gray-900">{t('search.children')}</p>
                                  <p className="text-sm text-gray-500">2-11 {t('search.searching') === 'Searching...' ? 'years' : 'años'}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => searchParams.children > 0 && setSearchParams({...searchParams, children: searchParams.children - 1})}
                                  disabled={searchParams.children <= 0}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <span className="text-lg font-bold w-8 text-center">{searchParams.children}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => totalPassengers < 13 && setSearchParams({...searchParams, children: searchParams.children + 1})}
                                  disabled={totalPassengers >= 13}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Babies */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Baby className="w-5 h-5 text-green-600" />
                                <div>
                                  <p className="font-semibold text-gray-900">{t('search.babies')}</p>
                                  <p className="text-sm text-gray-500">0-2 {t('search.searching') === 'Searching...' ? 'years' : 'años'}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => searchParams.babies > 0 && setSearchParams({...searchParams, babies: searchParams.babies - 1})}
                                  disabled={searchParams.babies <= 0}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <span className="text-lg font-bold w-8 text-center">{searchParams.babies}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => totalPassengers < 13 && setSearchParams({...searchParams, babies: searchParams.babies + 1})}
                                  disabled={totalPassengers >= 13}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>

                {searchParams.tripType === 'RT' && (
                  <div className="mb-6">
                    <Label htmlFor="passengers-rt" className="text-gray-700 font-semibold mb-2 block">
                      {t('search.passengers')}
                    </Label>
                    <Popover open={isPassengerPopoverOpen} onOpenChange={setIsPassengerPopoverOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="w-full md:w-1/4 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between bg-white"
                        >
                          <span className="text-gray-700">
                            {totalPassengers} {totalPassengers === 1 ? (t('search.searching') === 'Searching...' ? 'passenger' : 'pasajero') : (t('search.searching') === 'Searching...' ? 'passengers' : 'pasajeros')}
                          </span>
                          <Users className="w-5 h-5 text-gray-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-4" align="start">
                        <div className="space-y-4">
                          {/* Adults */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Users className="w-5 h-5 text-blue-900" />
                              <div>
                                <p className="font-semibold text-gray-900">{t('search.adults')}</p>
                                <p className="text-sm text-gray-500">{t('search.searching') === 'Searching...' ? '12+ years' : '12+ años'}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => searchParams.adults > 1 && setSearchParams({...searchParams, adults: searchParams.adults - 1})}
                                disabled={searchParams.adults <= 1}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="text-lg font-bold w-8 text-center">{searchParams.adults}</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => totalPassengers < 13 && setSearchParams({...searchParams, adults: searchParams.adults + 1})}
                                disabled={totalPassengers >= 13}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Children */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <User className="w-5 h-5 text-amber-600" />
                              <div>
                                <p className="font-semibold text-gray-900">{t('search.children')}</p>
                                <p className="text-sm text-gray-500">2-11 {t('search.searching') === 'Searching...' ? 'years' : 'años'}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => searchParams.children > 0 && setSearchParams({...searchParams, children: searchParams.children - 1})}
                                disabled={searchParams.children <= 0}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="text-lg font-bold w-8 text-center">{searchParams.children}</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => totalPassengers < 13 && setSearchParams({...searchParams, children: searchParams.children + 1})}
                                disabled={totalPassengers >= 13}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Babies */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Baby className="w-5 h-5 text-green-600" />
                              <div>
                                <p className="font-semibold text-gray-900">{t('search.babies')}</p>
                                <p className="text-sm text-gray-500">0-2 {t('search.searching') === 'Searching...' ? 'years' : 'años'}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => searchParams.babies > 0 && setSearchParams({...searchParams, babies: searchParams.babies - 1})}
                                disabled={searchParams.babies <= 0}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="text-lg font-bold w-8 text-center">{searchParams.babies}</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => totalPassengers < 13 && setSearchParams({...searchParams, babies: searchParams.babies + 1})}
                                disabled={totalPassengers >= 13}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg py-6" disabled={isLoading}>
                  <Search className="w-5 h-5 mr-2" />
                  {isLoading ? t('search.searching') : t('search.searchButton')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Resultados de Búsqueda */}
      {hasSearched && (
        <section id="search-results" className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Vuelos de Ida */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {t('results.outbound')}
              </h2>
              <p className="text-gray-600 mb-6">
                {searchParams.origin} → {searchParams.destination} | {searchParams.date}
              </p>
              <p className="text-gray-600 mb-4">
                {searchResults.length > 0 ? `${searchResults.length} ${t('results.found')}` : ''}
              </p>
            </div>

            {searchResults.length === 0 ? (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-8 text-center">
                  <AlertCircle className="w-16 h-16 text-amber-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('results.noFlights')}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {t('results.noFlightsMessage')}
                  </p>
                  <div className="bg-white rounded-lg p-4 text-sm text-left max-w-md mx-auto">
                    <p className="font-semibold text-gray-900 mb-2">{t('results.suggestions')}</p>
                    <ul className="space-y-1 text-gray-600">
                      <li>• {t('results.checkRoute')}</li>
                      <li>• {t('results.checkSchedule')}</li>
                      <li>• {t('results.tryAnother')}</li>
                    </ul>
                  </div>
                  <div className="mt-6">
                    <a href="tel:+529515020840" className="text-blue-900 hover:text-blue-700 font-semibold">
                      +52 951 502 0840
                    </a>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6 mb-12">
                {searchResults.map((flight) => (
                <Card key={flight.id} className="overflow-hidden hover:shadow-xl transition-all border-0">
                  <CardContent className="p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
                      {/* Información del Vuelo */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg flex items-center justify-center">
                            <Plane className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">{t('results.flight')} {flight.flightNumber}</div>
                            <div className="text-sm text-gray-600">{flight.aircraft}</div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-8">
                          <div>
                            <div className="text-3xl font-bold text-gray-900">{flight.departureTime}</div>
                            <div className="text-sm text-gray-600">{flight.origin}</div>
                          </div>

                          <div className="flex-1 flex flex-col items-center">
                            <div className="w-full border-t-2 border-dashed border-gray-300 relative">
                              <Plane className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-blue-900 rotate-90 bg-gray-50" />
                            </div>
                            <div className="text-sm text-gray-500 mt-2">45 min</div>
                          </div>

                          <div>
                            <div className="text-3xl font-bold text-gray-900">{flight.arrivalTime}</div>
                            <div className="text-sm text-gray-600">{flight.destination}</div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            flight.availableSeats > 5 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {flight.availableSeats} {t('results.availableSeats')}
                          </span>
                        </div>
                      </div>

                      {/* Precio y CTA */}
                      <div className="lg:text-right flex flex-col items-start lg:items-end space-y-4">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">{t('results.pricePerPerson')}</div>
                          <div className="text-4xl font-bold text-blue-900">
                            ${flight.price.toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-500">MXN</div>
                        </div>

                        <Button 
                          size="lg" 
                          className="w-full lg:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                          onClick={() => handleBooking(flight, false)}
                        >
                          {searchParams.tripType === 'RT' ? t('results.selectOutbound') : t('results.bookNow')}
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}

            {/* Vuelos de Regreso (solo si es viaje redondo) */}
            {searchParams.tripType === 'RT' && returnFlights.length > 0 && (
              <>
                <div className="mb-8 mt-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {t('results.returnFlights')}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {searchParams.destination} → {searchParams.origin} | {searchParams.returnDate}
                  </p>
                  <p className="text-gray-600 mb-4">
                    {t('results.foundFlights').replace('{count}', returnFlights.length)}
                  </p>
                </div>

                <div className="space-y-6">
                  {returnFlights.map((flight) => (
                    <Card key={flight.id} className="overflow-hidden hover:shadow-xl transition-all border-0">
                      <CardContent className="p-8">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
                          {/* Información del Vuelo */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-4 mb-4">
                              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                                <Plane className="w-6 h-6 text-white rotate-180" />
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">{t('results.flight')} {flight.flightNumber}</div>
                                <div className="text-sm text-gray-600">{flight.aircraft}</div>
                                <div className="text-xs text-amber-600 font-semibold">{t('results.returnLabel')}</div>
                              </div>
                            </div>

                            <div className="flex items-center space-x-8">
                              <div>
                                <div className="text-3xl font-bold text-gray-900">{flight.departureTime}</div>
                                <div className="text-sm text-gray-600">{flight.origin}</div>
                              </div>

                              <div className="flex-1 flex flex-col items-center">
                                <div className="w-full border-t-2 border-dashed border-gray-300 relative">
                                  <Plane className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-amber-600 rotate-90 bg-gray-50" />
                                </div>
                                <div className="text-sm text-gray-500 mt-2">45 min</div>
                              </div>

                              <div>
                                <div className="text-3xl font-bold text-gray-900">{flight.arrivalTime}</div>
                                <div className="text-sm text-gray-600">{flight.destination}</div>
                              </div>
                            </div>

                            <div className="mt-4">
                              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                flight.availableSeats > 5 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-amber-100 text-amber-800'
                              }`}>
                                {flight.availableSeats} {t('results.availableSeats')}
                              </span>
                            </div>
                          </div>

                          {/* Precio y CTA */}
                          <div className="lg:text-right flex flex-col items-start lg:items-end space-y-4">
                            <div>
                              <div className="text-sm text-gray-500 mb-1">{t('results.pricePerPerson')}</div>
                              <div className="text-4xl font-bold text-amber-600">
                                ${flight.price.toLocaleString()}
                              </div>
                              <div className="text-sm text-gray-500">MXN</div>
                            </div>

                            <Button 
                              size="lg" 
                              className="w-full lg:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                              onClick={() => handleBooking(flight, true)}
                              disabled={!selectedOutboundFlight}
                            >
                              {t('results.selectReturn')}
                              <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Resumen de Viaje Redondo */}
                <div className="mt-12 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('results.roundTripSummary')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg p-6">
                      <div className="text-sm text-gray-500 mb-2">{t('results.outbound')}</div>
                      <div className="text-lg font-bold text-blue-900">{searchParams.origin} → {searchParams.destination}</div>
                      <div className="text-sm text-gray-600">{searchParams.date}</div>
                    </div>
                    <div className="bg-white rounded-lg p-6">
                      <div className="text-sm text-gray-500 mb-2">{t('results.return')}</div>
                      <div className="text-lg font-bold text-amber-600">{searchParams.destination} → {searchParams.origin}</div>
                      <div className="text-sm text-gray-600">{searchParams.returnDate}</div>
                    </div>
                    <div className="bg-white rounded-lg p-6">
                      <div className="text-sm text-gray-500 mb-2">Total por {totalPassengers} {totalPassengers === 1 ? (t('search.searching') === 'Searching...' ? 'passenger' : 'pasajero') : (t('search.searching') === 'Searching...' ? 'passengers' : 'pasajeros')}</div>
                      <div className="text-3xl font-bold text-green-600">
                        ${((searchResults[0]?.price || 0) * 2 * totalPassengers).toLocaleString()} MXN
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{t('search.searching') === 'Searching...' ? 'Outbound + Return' : 'Ida + Regreso'}</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Rutas Populares */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('routes.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('routes.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {routes.map((route) => (
              <Card key={route.id} className="overflow-hidden hover:shadow-xl transition-all border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg flex items-center justify-center">
                        <Plane className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{route.origin}</div>
                        <div className="text-sm text-gray-500">{route.destination}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-900">${route.price.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">MXN</div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">{route.description}</p>

                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-gray-500">{t('results.duration')}:</span>
                      <span className="ml-2 font-semibold text-gray-900">{route.duration}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('search.searching') === 'Searching...' ? 'Frequency' : 'Frecuencia'}:</span>
                      <span className="ml-2 font-semibold text-gray-900">{route.frequency}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vuelos Privados CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(/images/aircraft4.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20">
                <Plane className="w-4 h-4 text-amber-400" />
                <span className="text-white text-sm font-medium">Cessna 208 Grand Caravan</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                ¿Necesitas un Vuelo Privado?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Vuela a cualquier estado de México con total flexibilidad y privacidad. 
                Desde $1,700 USD por hora de vuelo.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Elige tu horario y destino',
                  'Hasta 13 pasajeros',
                  'Sin escalas ni esperas',
                  'Equipaje flexible'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                    <span className="text-blue-100">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/vuelos-privados">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-6 shadow-2xl">
                  Conocer Más sobre Vuelos Privados
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="text-6xl font-bold text-amber-400 mb-2">
                    $1,700 <span className="text-3xl">USD</span>
                  </div>
                  <div className="text-xl text-blue-100">Por hora de vuelo</div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-blue-100">Ciudad de México</span>
                    <span className="font-bold text-white">~$1,700 USD</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-blue-100">Guadalajara</span>
                    <span className="font-bold text-white">~$4,250 USD</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-blue-100">Monterrey</span>
                    <span className="font-bold text-white">~$6,800 USD</span>
                  </div>
                  <div className="text-sm text-blue-200 text-center pt-2">
                    *Precios aproximados. Cotización exacta según ruta.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Booking Modal */}
      <BookingModalNew
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setSelectedFlight(null);
          setSelectedOutboundFlight(null);
          setSelectedReturnFlight(null);
        }}
        flight={searchParams.tripType === 'OW' ? selectedFlight : selectedOutboundFlight}
        tripType={searchParams.tripType}
        outboundFlight={selectedOutboundFlight}
        returnFlight={selectedReturnFlight}
        adults={searchParams.adults}
        children={searchParams.children}
        babies={searchParams.babies}
      />
    </div>
  );
};

export default RoutesPage;
